import {
    Comment,
    CommentWithMentionsAndUser,
    Note,
    NoteWithDetails,
} from '@/common/types/databaseTypes';
import { commentService } from '@/features/comment/services/commentService';
import { activityService } from '@/features/goal/services/activityService';
import { NotificationType } from '@/features/notification/constants/notification';
import { useMentionNotification } from '@/features/notification/services/factoryNotificationServices';
import { useDeleteNotificationWithRelatedData } from '@/features/notification/services/useNotificationQueries';
import { useSafeQuery } from '@/common/hooks/useSafeQuery';
import { supabase } from '@tickitacka/shared';
import { useAuthStore } from '@/stores/authStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
    EditorMention,
    extractMentionsFromNoteContent,
    findMentionInArray,
} from '../ui';
import { CreateNoteInput, noteService, ShareLinkData } from './noteServices';

interface UpdateNoteParams {
    noteId: string;
    content: string;
    markdownContent?: string;
    enabled?: boolean;
    targetId?: string;
}
interface UpdateNoteContentContext {
    noteTitle: string;
    oldNote: Note;
    oldContent: string;
    oldMentions: EditorMention[];
    newMentions: EditorMention[];
    addedMentions: EditorMention[];
    removedMentions: EditorMention[];
}

// 노트 상세 조회 훅
export function useNote(noteId: string | undefined) {
    return useSafeQuery<NoteWithDetails | null>({
        queryKey: ['note', noteId],
        queryFn: () =>
            noteId
                ? noteService.getNoteWithDetailsById(noteId)
                : Promise.resolve(null),
        enabled: !!noteId,
    });
}

// 투두 노트 목록 조회 훅
export function useNotesByTodoId(todoId: string) {
    return useSafeQuery<NoteWithDetails[], Error>({
        queryKey: ['notes', todoId],
        queryFn: () => noteService.getNoteByTodoId(todoId),
        enabled: !!todoId,
        refetchOnWindowFocus: 'always', // 새창으로 연 노트 업데이트 발생 시 캐시 무효화 필요
    });
}

// 노트 제목 수정 뮤테이션 훅
export function useUpdateNote() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            noteId,
            title,
            enabled,
            targetId, // todo/goal id - 업데이트 후 캐시 무효화 키에 필요
        }: {
            noteId: string;
            title: string;
            enabled?: boolean;
            targetId?: string;
        }) => noteService.updateNote(noteId, { title, enable: enabled }),
        onSuccess: async (_, { noteId, targetId }) => {
            // 노트 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['note', noteId] });
            await queryClient.invalidateQueries({
                queryKey: ['notes', targetId],
            });
            // 관련 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['planners'] });
        },
    });
}

// 노트 내용 업데이트 뮤테이션 훅
export function useUpdateNoteContent() {
    const { user } = useAuthStore.getState();
    const queryClient = useQueryClient();

    // 알림 발송, 삭제 훅
    const { createMentionNotification } = useMentionNotification();
    const deleteNotification = useDeleteNotificationWithRelatedData();

    return useMutation<Note, Error, UpdateNoteParams, UpdateNoteContentContext>(
        {
            mutationFn: ({
                noteId,
                content,
                markdownContent,
                enabled,
                targetId,
            }: UpdateNoteParams) =>
                noteService.updateNoteContent(
                    noteId,
                    content,
                    markdownContent,
                    enabled !== undefined ? enabled : content.trim() !== ''
                ),

            onMutate: async variables => {
                // 업데이트 전 기존 노트 데이터 가져오기
                const oldNote = await noteService.getNoteById(variables.noteId);
                const oldContent = oldNote?.content || '';

                // 이전 멘션과 새 멘션 추출
                const oldMentions = extractMentionsFromNoteContent(oldContent);
                const newMentions = extractMentionsFromNoteContent(
                    variables.content || ''
                );

                // 이전 멘션과 새 멘션 비교
                const addedMentions = newMentions.filter(
                    mention => !findMentionInArray(mention, oldMentions)
                );
                const removedMentions = oldMentions.filter(
                    mention => !findMentionInArray(mention, newMentions)
                );

                return {
                    noteTitle: oldNote?.title || '',
                    oldNote,
                    oldContent,
                    oldMentions,
                    newMentions,
                    addedMentions,
                    removedMentions,
                };
            },
            onSuccess: async (
                _,
                { noteId, targetId },
                { noteTitle, addedMentions, removedMentions }
            ) => {
                // 노트 캐시 무효화
                await queryClient.invalidateQueries({
                    queryKey: ['note', noteId],
                });
                await queryClient.invalidateQueries({
                    queryKey: ['notes', targetId],
                });

                const targetAddedMentions = addedMentions.filter(
                    mention =>
                        mention.userId !== user?.id &&
                        mention.mentionerId === user?.id
                );

                const notificationPromises = [];
                // 추가된 멘션 알림 발송
                notificationPromises.push(
                    ...targetAddedMentions.map(mention =>
                        createMentionNotification({
                            userId: mention.userId,
                            mentionContent: {
                                mention_id: mention.noteBlockId,
                                mentioner_name: user?.name,
                                mentioner_image_url: user?.profile_image_url,
                                mentioner_id: user?.id,
                                // mention_text: mention.mentionText,
                                context_title: noteTitle,
                                context_id: noteId,
                                context_type: 'note',
                            },
                        })
                    )
                );

                // 삭제된 멘션 알림 삭제
                notificationPromises.push(
                    ...removedMentions.map(mention =>
                        deleteNotification.mutateAsync({
                            userId: mention.userId,
                            relatedId: noteId,
                            type: NotificationType.MENTION,
                            registerId: mention.mentionerId,
                        })
                    )
                );

                // 알림 처리
                await Promise.all(notificationPromises);
            },
            onError: async (_, { noteId, targetId }) => {
                // 노트 캐시 무효화
                await queryClient.invalidateQueries({
                    queryKey: ['note', noteId],
                });
                await queryClient.invalidateQueries({
                    queryKey: ['notes', targetId],
                });
            },
        }
    );
}

// 노트 삭제 뮤테이션 훅
export function useDeleteNote() {
    const queryClient = useQueryClient();
    const deleteNoteActivity = useDeleteNoteActivity();

    return useMutation({
        mutationFn: (noteId: string) => {
            console.log(`노트 삭제 요청: ${noteId}`);
            return noteService.deleteNote(noteId);
        },
        onSuccess: async (_, noteId) => {
            // 노트 액티비티 삭제
            await deleteNoteActivity.mutateAsync(noteId);

            // 삭제 성공 시 캐시 무효화
            // 특정 노트 캐시 제거 (무효화가 아닌 제거)
            queryClient.removeQueries({ queryKey: ['note', noteId] });

            // 노트 목록 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['notes'] });

            // 관련 캐시 무효화
            await queryClient.invalidateQueries({ queryKey: ['planners'] });
            await queryClient.invalidateQueries({
                queryKey: ['todos', 'inbox'],
            });
        },
        onError: error => {
            console.error('노트 삭제 실패:', error);
        },
    });
}

// 노트 생성 뮤테이션 훅 (Optimistic Update 적용)
export function useCreateNote() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (params: CreateNoteInput) => noteService.createNote(params),
        onMutate: async newNoteParams => {
            // 낙관적 업데이트를 위해 쿼리를 일시 중지
            await queryClient.cancelQueries({ queryKey: ['notes'] });

            // 임시 ID 생성
            const tempId = 'temp_' + Date.now();

            // 임시 노트 객체 생성
            const now = new Date().toISOString();
            const optimisticNote = {
                id: tempId,
                ...newNoteParams,
                register_date: now,
                update_date: now,
                enable: true,
                content: newNoteParams.content || '',
                workspace_id: newNoteParams.workspace_id || '',
            } as Note;

            // 임시 노트를 캐시에 저장
            queryClient.setQueryData(['note', tempId], optimisticNote);

            // 관련 노트 목록에도 임시 노트 추가
            if (newNoteParams.goal_id) {
                const previousNotes =
                    queryClient.getQueryData<Note[]>([
                        'notes',
                        newNoteParams.goal_id,
                    ]) || [];
                queryClient.setQueryData(
                    ['notes', newNoteParams.goal_id],
                    [...previousNotes, optimisticNote]
                );
            }

            if (newNoteParams.todo_id) {
                const previousNotes =
                    queryClient.getQueryData<NoteWithDetails[]>([
                        'notes',
                        newNoteParams.todo_id,
                    ]) || [];
                queryClient.setQueryData(
                    ['notes', newNoteParams.todo_id],
                    [...previousNotes, optimisticNote as NoteWithDetails]
                );
            }

            // 롤백을 위해 이전 상태와 임시 ID 반환
            return {
                tempId,
                previousGoalNotes: newNoteParams.goal_id
                    ? queryClient.getQueryData<Note[]>([
                          'notes',
                          newNoteParams.goal_id,
                      ])
                    : undefined,
                previousTodoNotes: newNoteParams.todo_id
                    ? queryClient.getQueryData<NoteWithDetails[]>([
                          'notes',
                          newNoteParams.todo_id,
                      ])
                    : undefined,
            };
        },
        onSuccess: (newNote, _, context) => {
            if (context?.tempId) {
                // 임시 노트를 캐시에서 제거
                queryClient.removeQueries({
                    queryKey: ['note', context.tempId],
                });
            }

            // 실제 노트 데이터를 캐시에 저장
            queryClient.setQueryData(['note', newNote.id], newNote);

            // 백그라운드에서 캐시 무효화
            void queryClient.invalidateQueries({ queryKey: ['notes'] });
            void queryClient.invalidateQueries({ queryKey: ['planners'] });
            void queryClient.invalidateQueries({
                queryKey: ['todos', 'inbox'],
            });
        },
        onError: (error, newNoteParams, context) => {
            // 오류 발생 시 로그 기록
            console.error('노트 생성 실패:', error);

            // 롤백 처리
            if (context?.tempId) {
                // 임시 노트를 캐시에서 제거
                queryClient.removeQueries({
                    queryKey: ['note', context.tempId],
                });
            }

            // 노트 목록 롤백
            if (newNoteParams.goal_id && context?.previousGoalNotes) {
                queryClient.setQueryData(
                    ['notes', newNoteParams.goal_id],
                    context.previousGoalNotes
                );
            }

            if (newNoteParams.todo_id && context?.previousTodoNotes) {
                queryClient.setQueryData(
                    ['notes', newNoteParams.todo_id],
                    context.previousTodoNotes
                );
            }
        },
        onSettled: () => {
            // 성공/실패 관계 없이 최종적으로 데이터 다시 조회
            void queryClient.invalidateQueries({ queryKey: ['notes'] });
        },
    });
}

// 공유 링크로 노트 조회 훅
export function useNoteByShortCode(shortCode: string | undefined) {
    return useQuery<Note | null>({
        queryKey: ['note', 'shortCode', shortCode],
        queryFn: () =>
            shortCode
                ? noteService.getNoteByShortCode(shortCode)
                : Promise.resolve(null),
        enabled: !!shortCode,
    });
}

// 공유 링크 생성 뮤테이션 훅
export function useCreateShareLink() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: ShareLinkData) => noteService.createShareLink(data),
        onSuccess: async updatedNote => {
            // 노트 캐시 업데이트
            queryClient.setQueryData(['note', updatedNote.id], updatedNote);

            // 관련 캐시 무효화
            await queryClient.invalidateQueries({
                queryKey: ['note', updatedNote.id],
            });
        },
        onError: error => {
            console.error('공유 링크 생성 실패:', error);
        },
    });
}

// 공유 링크 비활성화 뮤테이션 훅
export function useDisableShareLink() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (noteId: string) => noteService.disableShareLink(noteId),
        onSuccess: async updatedNote => {
            // 노트 캐시 업데이트
            queryClient.setQueryData(['note', updatedNote.id], updatedNote);

            // 관련 캐시 무효화
            await queryClient.invalidateQueries({
                queryKey: ['note', updatedNote.id],
            });
        },
        onError: error => {
            console.error('공유 링크 비활성화 실패:', error);
        },
    });
}

// 마크다운만 업데이트하는 뮤테이션 훅
export function useUpdateNoteMarkdownOnly() {
    const queryClient = useQueryClient();

    return useMutation<
        Note,
        Error,
        { noteId: string; markdownContent: string }
    >({
        mutationFn: ({ noteId, markdownContent }) =>
            noteService.updateNoteMarkdownOnly(noteId, markdownContent),
        onSuccess: async updatedNote => {
            // 캐시에 저장된 기존 노트 구조를 보존하면서 markdown 필드만 갱신합니다.
            queryClient.setQueryData<Note | undefined>(
                ['note', updatedNote.id],
                old => {
                    if (!old) return old;
                    return {
                        ...old,
                        markdown_content: updatedNote.markdown_content,
                        update_date: updatedNote.update_date,
                    } as Note;
                }
            );

            // 서버의 최신 상태를 동기화하기 위해 쿼리를 무효화합니다.
            await queryClient.invalidateQueries({
                queryKey: ['note', updatedNote.id],
            });

            console.log(
                '[useUpdateNoteMarkdownOnly] Markdown updated successfully'
            );
        },
        onError: error => {
            console.error(
                '[useUpdateNoteMarkdownOnly] Failed to update markdown:',
                error
            );
        },
    });
}

// 노트 액티비티 삭제 훅
function useDeleteNoteActivity() {
    return useMutation({
        mutationFn: (noteId: string) =>
            activityService.deleteActivityLog({
                activityType: 'note',
                referenceId: noteId,
            }),
    });
}

/**
 * 노트 댓글을 실시간으로 스트리밍하는 훅
 *
 * @param noteId 노트 ID
 * @returns 실시간으로 업데이트되는 댓글 목록, 댓글 개수, 로딩 상태
 */
export function useStreamNoteComments(noteId: string | undefined) {
    const [comments, setComments] = useState<CommentWithMentionsAndUser[]>([]);
    const [commentCount, setCommentCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState(true);
    const queryClient = useQueryClient();

    // 댓글 조회 쿼리 훅 사용 (캐시 관리를 위해)
    const { data: cachedComments } = useSafeQuery<CommentWithMentionsAndUser[]>(
        {
            queryKey: ['noteComments', noteId],
            queryFn: () =>
                noteId
                    ? commentService.getComments('note_id', noteId)
                    : Promise.resolve([]),
            enabled: !!noteId,
            staleTime: 1000 * 60 * 5, // 5분 동안 캐시 유지
        }
    );

    // 댓글 개수 조회 쿼리 훅 사용
    const { data: cachedCommentCount } = useQuery<number>({
        queryKey: ['noteComments', 'count', noteId],
        queryFn: () =>
            noteId
                ? commentService.getCommentsCount('note_id', noteId)
                : Promise.resolve(0),
        enabled: !!noteId,
        staleTime: 1000 * 60 * 5, // 5분 동안 캐시 유지
    });

    // 초기 데이터 설정
    useEffect(() => {
        if (cachedComments) {
            setComments(cachedComments);
            setIsLoading(false);
        }
    }, [cachedComments]);

    // 초기 댓글 개수 설정
    useEffect(() => {
        if (cachedCommentCount !== undefined) {
            setCommentCount(cachedCommentCount);
        }
    }, [cachedCommentCount]);

    // Supabase 실시간 구독 설정
    useEffect(() => {
        if (!noteId) {
            setComments([]);
            setIsLoading(false);
            return;
        }

        // 초기 데이터가 없을 때만 로딩 상태 표시
        if (!cachedComments) {
            setIsLoading(true);
        }

        // 디바운스 처리 로직
        let debounceTimer: NodeJS.Timeout | null = null;

        // 댓글 개수 업데이트 함수
        const updateCommentCount = async () => {
            if (!noteId) return;
            try {
                const count = await commentService.getCommentsCount(
                    'note_id',
                    noteId
                );
                setCommentCount(count);
            } catch (error) {
                console.error('댓글 개수 조회 오류:', error);
            }
        };

        // 초기 댓글 개수 가져오기
        void updateCommentCount();

        const debouncedInvalidate = () => {
            if (debounceTimer) clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                // 댓글 목록 쿼리 무효화
                void queryClient.invalidateQueries({
                    queryKey: ['noteComments', noteId],
                });

                // 댓글 개수 직접 업데이트
                void updateCommentCount();
            }, 300); // 300ms 디바운스
        };

        // Supabase 채널 설정
        const channel = supabase.channel(`note-comments-${noteId}-realtime`);

        // INSERT 이벤트 감지 - 새 댓글 추가
        channel.on(
            'postgres_changes',
            {
                event: 'INSERT',
                schema: 'public',
                table: 'comment',
                filter: `note_id=eq.${noteId}`,
            },
            payload => {
                void debouncedInvalidate();
            }
        );

        // DELETE 이벤트 감지 - 댓글 삭제
        channel.on(
            'postgres_changes',
            {
                event: 'DELETE',
                schema: 'public',
                table: 'comment',
                filter: `note_id=eq.${noteId}`,
            },
            payload => {
                void debouncedInvalidate();
            }
        );

        // UPDATE 이벤트 처리 - 댓글 수정
        // 중요한 필드 변경시에만 업데이트
        const relevantFields = ['content', 'enable'];
        const hasRelevantChanges = (
            old: Partial<Comment>,
            new_: Partial<Comment>
        ) =>
            relevantFields.some(
                field =>
                    old[field as keyof Partial<Comment>] !==
                    new_[field as keyof Partial<Comment>]
            );

        channel.on(
            'postgres_changes',
            {
                event: 'UPDATE',
                schema: 'public',
                table: 'comment',
                filter: `note_id=eq.${noteId}`,
            },
            payload => {
                if (
                    hasRelevantChanges(
                        payload.old as Partial<Comment>,
                        payload.new as Partial<Comment>
                    )
                ) {
                    void debouncedInvalidate();
                }
            }
        );

        // Supabase 구독 시작
        const subscription = channel.subscribe((status: string) => {
            if (status === 'SUBSCRIBED') {
                console.log(`Subscribed to goal-${noteId}-realtime`);
            } else if (status === 'CHANNEL_ERROR') {
                console.error(`Error subscribing to goal-${noteId}-realtime`);
            }
        });

        // 클린업 함수
        return () => {
            try {
                void subscription.unsubscribe();
            } catch (error: unknown) {
                console.error(
                    '구독 해제 중 오류 발생:',
                    error instanceof Error ? error.message : '알 수 없는 오류'
                );
            }
        };
    }, [noteId, queryClient, cachedComments]);

    return { comments, commentCount, isLoading };
}
